import React, { useState, useEffect, useRef, useCallback } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import styled from "styled-components";
import { parse, isValid, format, startOfToday } from "date-fns";
import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import { CalendarIcon } from "../../atoms/icons/CalendarIcon";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Small from "../../atoms/typography/Small";

interface Props {
  onChange: (date: Date | undefined) => void;
  errorMessage?: string;
  value?: Date;
  disableDatesBeforeToday?: boolean;
  disableFutureDates?: boolean;
  maxDate?: Date;
  title?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

const TextFieldContainer = styled.div<{
  error?: boolean;
  fullWidth?: boolean;
  disbaled?: boolean;
}>`
  display: flex;
  align-items: center;
  background-color: transparent;
  padding: 4px;
  border-bottom: ${(props) =>
    props.error
      ? `1.25px solid ${theme.colors.errorDark}`
      : props.disbaled
        ? "none"
        : `1.25px solid ${theme.colors.grey40}`};
  width: ${(props) => (props.fullWidth ? "100%" : "fit-content")};
  box-sizing: border-box;
  cursor: pointer;
  height: 36px;

  &:focus {
    border: 1px solid #6a7789;
    color: #6a7789;
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    border: none;
  }
`;

const TextField = styled.input`
  width: 100%;
  border: none;
  font-family: Inter;
  color: ${theme.colors.text};
  background-color: transparent;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DatePickerWrapper = styled.div<{ position: string }>`
  position: absolute;
  z-index: 1000;
  background-color: white;
  width: fit-content;
  box-shadow: 0px 4px 12px 10px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  font-family: "Inter", sans-serif;
  padding: 16px;
  color: ${theme.colors.text};
  font-size: 16px;
  ${({ position }) => position === "top" && "bottom: 100%"};
`;

const StyledDatePicker = styled(DayPicker)`
  --rdp-accent-color: ${theme.colors.primary};
  --rdp-background-color: #0f5156; /* Ensures full color */
  --rdp-accent-light: rgba(15, 81, 86, 0.2);
  --rdp-accent-background-color: ${theme.colors.primary60};

  .rdp-day_selected,
  .rdp-day_selected:hover {
    background-color: var(--rdp-accent-color) !important;
    color: white !important;
    border-radius: 5px;
  }
`;

const DatePicker: React.FC<Props> = ({
  onChange,
  errorMessage,
  value,
  disableDatesBeforeToday,
  disableFutureDates,
  maxDate,
  title,
  fullWidth,
  disabled,
}) => {
  const formatDate = useCallback((date: Date | undefined) => {
    return date ? format(date, "yyyy-MM-dd") : "";
  }, []);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ?? startOfToday(),
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [position, setPosition] = useState("bottom");
  const [inputValue, setInputValue] = useState<string>(
    value ? formatDate(value) : formatDate(startOfToday()),
  );
  const [invalidDateMessage, setInvalidDateMessage] = useState<
    string | undefined
  >(undefined);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (value !== undefined) {
      setSelectedDate(value);
      setInputValue(formatDate(value));
    }
  }, [value, formatDate]);

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (!value) setSelectedDate(date);
      setInputValue(formatDate(date));
      onChange(date);
      setIsDatePickerOpen(false);
      setInvalidDateMessage("");
    },
    [onChange, formatDate, value],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      setInputValue(val);
      const inputDate = parse(val, "yyyy-MM-dd", new Date());
      if (isValid(inputDate)) {
        if (!value) setSelectedDate(inputDate);
        onChange(inputDate);
        setInvalidDateMessage("");
      } else {
        if (!value) setSelectedDate(undefined);
        setInvalidDateMessage(t("dates.invalidDateFormat"));
      }
    },
    [onChange, t, value],
  );

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      datePickerRef.current &&
      !datePickerRef.current.contains(event.target as Node)
    ) {
      setIsDatePickerOpen(false);
    }
  }, []);

  const handleOpenDatePicker = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (rect.bottom + 300 > windowHeight) {
        // 300 is an approximate height of the date picker
        setPosition("top");
      } else {
        setPosition("bottom");
      }
    }
    setIsDatePickerOpen(true);
  }, []);

  useEffect(() => {
    if (isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen, handleClickOutside]);

  const hasError = !!errorMessage || !!invalidDateMessage;

  return (
    <div style={{ position: "relative" }}>
      {title && <Small color={theme.colors.textSecondary}>{title}</Small>}
      {title && <HorizontalSpacer spacing={0.25} />}
      <TextFieldContainer
        error={hasError ? true : undefined}
        onClick={disabled ? undefined : handleOpenDatePicker}
        fullWidth={fullWidth}
        disbaled={disabled}
      >
        <TextField
          ref={inputRef}
          type="text"
          value={value ? inputValue : "Välj datum"}
          placeholder={t("dates.dateFormat")}
          onChange={handleInputChange}
          disabled={disabled}
        />
        <CalendarIcon />
      </TextFieldContainer>
      {isDatePickerOpen && (
        <DatePickerWrapper ref={datePickerRef} position={position}>
          <StyledDatePicker
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            numberOfMonths={1}
            disabled={
              disableDatesBeforeToday
                ? [{ before: startOfToday() }]
                : disableFutureDates
                  ? [{ after: startOfToday() }]
                  : maxDate
                    ? [{ after: maxDate }]
                    : undefined
            }
          />
        </DatePickerWrapper>
      )}
      {invalidDateMessage ? (
        <BodySmall color={theme.colors.error}>{invalidDateMessage}</BodySmall>
      ) : (
        errorMessage && (
          <BodySmall color={theme.colors.error}>{errorMessage}</BodySmall>
        )
      )}
    </div>
  );
};

export default DatePicker;
